<template>
  <span
    :class="avatarClasses"
    :style="avatarStyle"
    @click="$emit('click', $event)">
    <slot>
      <v-icon
        v-if="hasIcon"
        :class="['cy-avatar__icon', `${iconStyle.name}-color`]"
        :color="iconColor || $static.defaultIconColor"
        :disabled="disabled"
        :size="iconSize">
        {{ item.icon || $static.defaultIcon }}
      </v-icon>
      <v-icon
        v-else-if="isPendingMember"
        color="#C6D2E3"
        :size="pendingIconSize">
        mdi-account-circle
      </v-icon>
      <CyAsyncImage
        v-else
        :src="item?.picture_url"
        alt="avatar"
        :class="imageClass"
        @load="profileImageExists = $event">
        <template #fallback>
          <span>
            {{ initials }}
          </span>
        </template>
      </CyAsyncImage>
    </slot>
  </span>
</template>

<script>
import { ENV_PRESETS, DEFAULT_ICON, DEFAULT_ICON_COLOR, DEFAULT_COLOR_NAME } from '@/utils/config/icons'

export const initialsKeys = ['full_name', 'username', 'name']
export const validKeys = ['picture_url', 'icon', 'color', ...initialsKeys]
export const bgColors = ['#008573', '#7232F9', '#61802C', '#003BF9', '#CB494D', '#D800BB', '#B85E00', '#AB52B4']

export default {
  name: 'CyAvatar',
  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    iconSize: {
      type: String,
      default: '24',
    },
    xs: {
      type: Boolean,
      default: false,
    },
    sm: {
      type: Boolean,
      default: false,
    },
    lg: {
      type: Boolean,
      default: false,
    },
    xl: {
      type: Boolean,
      default: false,
    },
    square: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['click'],
  data: () => ({
    profileImageExists: false,
  }),
  computed: {
    $static: () => ({
      defaultIcon: DEFAULT_ICON,
      defaultIconColor: DEFAULT_ICON_COLOR,
    }),
    avatarClasses () {
      return [
        'cy-avatar',
        this.initialsBackground,
        {
          'cy-avatar--x-small': this.xs,
          'cy-avatar--small': this.sm,
          'cy-avatar--large': this.lg,
          'cy-avatar--x-large': this.xl,
          'cy-avatar--is-image': this.profileImageExists,
          'cy-avatar--square': this.square,
        },
      ]
    },
    avatarStyle () {
      if (!this.hasIcon && !this.isPendingMember && this.initialsBackground) {
        return { 'background-color': this.initialsBackground }
      }
      return null
    },
    imageClass () {
      return this.profileImageExists ? 'cy-avatar__image' : 'cy-avatar__initials'
    },
    pendingIconSize () {
      if (this.xl) return '96'
      if (this.lg) return '48'
      if (this.sm) return '24'
      if (this.xs) return '16'
      return '32'
    },
    isPendingMember () {
      return !this.item?.username && this.item?.last_login_at === 0
    },
    fullName () {
      if (this.item?.full_name) {
        return this.item.full_name
      }
      return _.$getFirstPopulatedValue(this.item, validKeys) || ''
    },
    initials () {
      return this.fullName.split(' ').slice(0, 2).map((word) => word.charAt(0)).join('').toUpperCase()
    },
    initialsBackground () {
      const asciiSum = this.fullName.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0)
      return bgColors[asciiSum % bgColors.length]
    },
    iconStyle () {
      return _.find(ENV_PRESETS, { name: this.item.color }) || _.find(ENV_PRESETS, { name: DEFAULT_COLOR_NAME })
    },
    iconColor () {
      return this.iconStyle?.foreground || DEFAULT_ICON_COLOR
    },
    hasIcon () {
      return _.has(this.item, 'icon')
    },
  },
}
</script>

<style lang="scss" scoped>
$x-small-size: 16px;
$small-size: 24px;
$normal-size: 32px;
$large-size: 48px;
$x-large-size: 96px;

.cy-avatar {
  display: flex;
  place-content: center center;
  align-items: center;
  height: $normal-size;
  aspect-ratio: 1;
  border-radius: 50%;
  color: cy-get-color("white");

  &__image {
    width: 100%;
    height: 100%;
  }

  &__initials {
    font-size: 14px;
    font-weight: 800;
    text-transform: uppercase;
  }

  &__icon {
    position: relative;
    z-index: 0;
    width: $normal-size;
    height: $normal-size;
    padding: $spacer;
    border: 1px solid cy-get-color("white");
    border-radius: 8px;

    :deep(svg) {
      min-width: 18px;
      min-height: 18px;
    }

    .cy-avatar--square & {
      border-radius: 4px;
    }

    &.dev-color {
      background-color: map.get($slate-grey, "light-4");
    }

    &.prod-color {
      background-color: map.get($teal, "light-2");
    }

    &.success-color {
      background-color: map.get($green, "light-1");
    }

    &.error-color {
      background-color: map.get($red, "light-1");
    }

    &.default-color {
      background-color: map.get($brown, "light-3");
    }

    &.staging-color {
      background-color: map.get($orange, "light-2");
    }

    .cy-avatar--x-small & {
      width: $x-small-size;
      height: $x-small-size;
    }

    .cy-avatar--small & {
      width: $small-size;
      height: $small-size;
    }

    .cy-avatar--large & {
      width: $large-size;
      height: $large-size;
    }

    .cy-avatar--x-large & {
      width: $x-large-size;
      height: $x-large-size;
    }

    &::after {
      content: none;
    }
  }

  &--x-small {
    height: $x-small-size;

    .cy-avatar__initials {
      font-size: 9px;
    }
  }

  &--small {
    height: $small-size;

    .cy-avatar__initials {
      font-size: 10px;
    }
  }

  &--large {
    height: $large-size;

    .cy-avatar__initials {
      font-size: 20px;
    }
  }

  &--x-large {
    height: $x-large-size;

    .cy-avatar__initials {
      font-size: 40px;
    }
  }

  &--is-image {
    overflow: hidden;
  }
}
</style>
